import { useMemo } from 'react';
import { TranscriptItem as TranscriptItemModel } from '@/types/transcript';

export interface Phrase {
  words: TranscriptItemModel[];
  startIndex: number;
  endIndex: number;
  lineNumber: number;
}

/**
 * Groups transcript items into phrases by sentence boundaries (. ! ?).
 * Each phrase is a consecutive run of words until a sentence-ending punctuation.
 */
export function usePhrases(transcriptItems: TranscriptItemModel[]): {
  phrases: Phrase[];
  lineNumbersByWordIndex: number[];
} {
  return useMemo(() => {
    if (!transcriptItems.length) {
      return { phrases: [], lineNumbersByWordIndex: [] };
    }

    const phrases: Phrase[] = [];
    const lineNumbersByWordIndex: number[] = [];
    let currentPhrase: TranscriptItemModel[] = [];
    let currentLine = 1;
    let phraseStartIndex = 0;

    transcriptItems.forEach((item, index) => {
      const isSentenceEnd = item.text.match(/[.!?]$/);

      currentPhrase.push(item);
      lineNumbersByWordIndex.push(currentLine);

      if (isSentenceEnd) {
        phrases.push({
          words: currentPhrase,
          startIndex: phraseStartIndex,
          endIndex: index,
          lineNumber: currentLine,
        });
        currentPhrase = [];
        phraseStartIndex = index + 1;
        currentLine++;
      }
    });

    if (currentPhrase.length > 0) {
      phrases.push({
        words: currentPhrase,
        startIndex: phraseStartIndex,
        endIndex: transcriptItems.length - 1,
        lineNumber: currentLine,
      });
    }

    return { phrases, lineNumbersByWordIndex };
  }, [transcriptItems]);
}
